import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

import { useTranslation } from '@/utils/i18n';
import { STORE_URL_IOS, STORE_URL_ANDROID } from '@/constants';
import { openExternal } from '@/utils/browser';
import { PrimaryButton } from '@/components/Buttons';
import BottomSheet from '@/components/BottomSheet';

interface Props {
  /** The backend's latest_version — the popin shows once per value. */
  latestVersion: string;
}

/** Per-device flag prefix: the soft popin was dismissed for this version. */
const UPDATE_PROMPT_SEEN_PREFIX = 'update_prompt_seen_';

/**
 * E3 — dismissible "update available" popin. Shown once per latest_version
 * (Preferences-persisted), unlike the force gate which blocks every launch.
 * CTA deep-links to the store; a Capgo OTA reload path can slot in later
 * once the updater plugin ships in the native shell.
 */
export default function UpdatePopin({ latestVersion }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void Preferences.get({ key: UPDATE_PROMPT_SEEN_PREFIX + latestVersion }).then(({ value }) => {
      if (!cancelled && !value) setOpen(true);
    });
    return () => {
      cancelled = true;
    };
  }, [latestVersion]);

  const dismiss = useCallback(() => {
    setOpen(false);
    void Preferences.set({ key: UPDATE_PROMPT_SEEN_PREFIX + latestVersion, value: '1' });
  }, [latestVersion]);

  const openStore = useCallback(() => {
    const url = Capacitor.getPlatform() === 'ios' ? STORE_URL_IOS : STORE_URL_ANDROID;
    void openExternal(url);
    dismiss();
  }, [dismiss]);

  return (
    <BottomSheet
      open={open}
      onClose={dismiss}
      title={t('appUpdate.optional.title')}
      testid="sheet-update-popin"
    >
      <div className="tw:flex tw:flex-col tw:gap-4">
        <p className="tw:text-base tw:text-grey-2">{t('appUpdate.optional.message')}</p>
        <PrimaryButton
          label={t('appUpdate.updateButton')}
          onClick={openStore}
          data-testid="btn-soft-update"
        />
        <button
          type="button"
          onClick={dismiss}
          data-testid="btn-soft-update-later"
          className="tw:py-2 tw:text-base tw:text-grey-3"
        >
          {t('appUpdate.laterButton')}
        </button>
      </div>
    </BottomSheet>
  );
}
